import React from 'react';
import { Container, Typography, Box, Grid, Avatar } from '@mui/material';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

function About() {
  const { t } = useTranslation();

  // 🎞️ Variants pour animations fluides
  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 1, ease: 'easeOut' } },
  };

  const fadeLeft = {
    hidden: { opacity: 0, x: -60 },
    visible: { opacity: 1, x: 0, transition: { duration: 1.2, ease: 'easeOut' } },
  };

  const fadeRight = {
    hidden: { opacity: 0, x: 60 },
    visible: { opacity: 1, x: 0, transition: { duration: 1.2, ease: 'easeOut' } },
  };

  
  const circleVariants = {
    hidden: { opacity: 0, scale: 0.5 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.6, delay: 0.6 } },
  };


  return (
    <>
        <Helmet>
        <title>SPEEDY Transport & Logistics</title>
      </Helmet>

      <Box
        sx={{
          background: 'linear-gradient(135deg, #EAFDFC 0%, #C2F2EF 40%, #F5FFFA 100%)',
          py: { xs: 8, md: 12 },
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Container maxWidth={false} sx={{ my: 3, px: { xs: 2, md: 2 } }}>
          {/* 🌟 Titre principal */}
          <motion.div variants={fadeUp} initial="hidden" animate="visible">
            <Typography
              variant="h3"
              sx={{
                textAlign: 'center',
                fontWeight: 800,
                mb: -4,
                color: '#004D61',
                textTransform: 'uppercase',
                letterSpacing: 1.5,
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              {t('about.mainTitle')} <span style={{ color: '#0097A7' }}>Speedy TL</span>
            </Typography>
          </motion.div>

          {/* 🖼️ Image à gauche | 🧾 Texte à droite */}
          <Grid
  container
  spacing={4}
  justifyContent="space-between"
  alignItems="flex-start"
  sx={{
    flexWrap: 'nowrap', // 🚀 empêche le retour à la ligne
    overflowX: 'auto',  // 🔹 scroll horizontal si l’écran est trop petit
    position: 'relative',
    zIndex: 2,
    px: 2,
  }}
>

       

            {/* Texte */}
          {/* 🔹 Ligne d’histoire stylisée avec icônes et texte */}
<Box
  sx={{
    mt: 10,
    py: 6,
    backgroundColor: '#004D61',
    borderRadius: '24px',
    position: 'relative',
    overflow: 'hidden',
  }}
>
  <Container maxWidth={false} sx={{ my: 3, px: { xs: 2, md: 16 } }}>
    {/* Ligne horizontale */}
    <Box
      sx={{
        position: 'absolute',
        top: '60px',
        left: 0,
        width: '250%',
        height: '3px',
        backgroundColor: 'rgba(255, 255, 255, 0.4)',
      }}
    />

    <Grid container spacing={6} justifyContent="center" sx={{ position: 'relative', zIndex: 2 }}>
      {[
        {
  icon: '🚚',
  title: t('about.section1Title'),
  text: t('about.section1Text'),
},
{
  icon: '🏆',
  title: t('about.section2Title'),
  text: t('about.section2Text'),
},
{
  icon: '🌍',
  title: t('about.section3Title'),
  text: t('about.section3Text'),
},

      ].map((step, index) => (
       <Grid
  item
  xs={12}
  sm={4}
  md={4}
  key={index}
  sx={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  }}
>

          {/* Animation du cercle et du contenu */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: index * 0.2 }}
            viewport={{ once: true }}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
          >
            {/* Cercle icône */}
            <Box
              sx={{
                width: 70,
                height: 70,
                borderRadius: '50%',
                backgroundColor: '#00ACC1',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                fontSize: '2rem',
                color: 'white',
                mb: 3,
                zIndex: 3,
                boxShadow: '0 6px 15px rgba(0,0,0,0.2)',
              }}
            >
              {step.icon}
            </Box>

            {/* Carte blanche du texte */}
            <Box
              sx={{
                mt: 2,
                backgroundColor: 'white',
                borderRadius: '16px',
                p: 3,
                width: '50%',
                minHeight: 220,
                boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                textAlign: 'center',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: '#007B8F',
                  mb: 2,
                  fontFamily: "'Poppins', sans-serif",
                }}
              >
                {step.title}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#444',
                  fontSize: '0.95rem',
                  lineHeight: 1.7,
                  textAlign: 'justify',
                }}
              >
                {step.text}
              </Typography>
            </Box>
          </motion.div>
        </Grid>
      ))}
    </Grid>
  </Container>
</Box>

          </Grid>

          {/* 🏆 Valeurs de l’entreprise */}
          <motion.div variants={fadeUp} initial="hidden" animate="visible">
            <Typography
              variant="h4"
              sx={{
                textAlign: 'center',
                fontWeight: 700,
                mt: 10,
                mb: 6,
                color: '#004D61',
                textTransform: 'uppercase',
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              {t('about.valuesTitle')}
            </Typography>
          </motion.div>
 <Grid container spacing={4} justifyContent="center">
            {[
              { title: t('about.value1Title'), desc: t('about.value1Desc'), icon: '🚀' },
              { title: t('about.value2Title'), desc: t('about.value2Desc'), icon: '🔒' },
            ].map((value, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <motion.div
                  variants={circleVariants}
                  whileHover={{ scale: 1.1, transition: { duration: 0.3 } }} // Hover pour agrandir le cercle
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      p: 3,
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 120,
                        height: 120,
                        bgcolor: '#007B8F',
                                  fontSize: '3rem',
                        mb: 2,
                        boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                      }}
                    >
                      {value.icon}
                    </Avatar>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: '#007B8F',
                        mb: 1,
                        fontFamily: "'Poppins', sans-serif",
                      }}
                    >
                      {value.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#555',
                        lineHeight: 1.6,
                        maxWidth: 200,
                      }}
                    >
                      {value.desc}
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </>
  );
}

export default About;
